import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isEditable } from '../../enums/InvoiceStatusState';
import InvoiceStatusBadge from '../../components/InvoiceStatus/InvoiceStatusBadge';
import { toIdentifierMask } from '../../services/mask';
import InvoiceTagBadge from './InvoiceTagBadge';
import { InvoiceViewModel } from '../../models/Invoice/InvoiceViewModel';

interface InvoicesListProps {
  invoices: InvoiceViewModel[];
  isMultiselect?: boolean;
  openInvoiceInNewTab?: boolean;
  setInvoicesSelected?: (ids: number[]) => void;
}

export default function InvoicesList({
  invoices,
  setInvoicesSelected,
  isMultiselect = false,
  openInvoiceInNewTab = false,
}: InvoicesListProps) {
  const [invoicesToAdd, setInvoicesToAdd] = useState([] as InvoiceViewModel[]);
  const [selected, setSelected] = useState<number[]>([]);

  function formatCell(
    top: JSX.Element | string,
    bottom: JSX.Element | string = ''
  ) {
    return (
      <div className='d-flex flex-column'>
        <div>{top}</div>
        {bottom ? (
          <div>
            <small className='text-muted'>{bottom}</small>
          </div>
        ) : undefined}
      </div>
    );
  }

  const onChecked = (invoice: InvoiceViewModel): void => {
    invoice.toggleChecked();
    let updatedSelected = [...selected];
    if (updatedSelected.includes(invoice.id)) {
      updatedSelected = updatedSelected.filter((id) => id !== invoice.id);
    } else {
      updatedSelected.push(invoice.id);
    }

    setSelected(updatedSelected);
    if (setInvoicesSelected) {
      setInvoicesSelected(updatedSelected);
    }
  };

  useEffect(() => {
    if (invoices.length > 0) {
      setInvoicesToAdd(invoices.map((i) => new InvoiceViewModel(i)));
    }
  }, [invoices]);

  return (
    <table className='table table-hover'>
      <thead>
        <tr>
          {isMultiselect && <th scope='col'>&nbsp;</th>}
          <th scope='col'>#</th>
          <th scope='col'>Data</th>
          <th scope='col'>Empresa</th>
          <th scope='col'>Cliente</th>
          <th scope='col'>Natureza da Op.</th>
          <th scope='col'>Status</th>
          <th scope='col'>Etiquetas</th>
        </tr>
      </thead>
      <tbody>
        {invoicesToAdd.map((i) => {
          const createdAt = new Date(i.createdAt);
          const editLink = (
            <Link
              target={openInvoiceInNewTab ? '_blank' : '_self'}
              to={`/invoices/${i.id}/edit`}
            >
              {i.number || '#'}
            </Link>
          );
          return (
            <tr key={i.id}>
              {isMultiselect && (
                <td>
                  <input
                    className='form-check-input'
                    type='checkbox'
                    value={i.id}
                    defaultChecked={i.checked}
                    onClick={() => onChecked(i)}
                  />
                </td>
              )}
              <th scope='row'>{formatCell(editLink)}</th>
              <td>
                {formatCell(
                  createdAt.toLocaleDateString(),
                  createdAt.toLocaleTimeString()
                )}
              </td>
              <td>
                {formatCell(
                  i.companyName,
                  toIdentifierMask(i.companyIdentifier, i.companyType)
                )}
              </td>
              <td>
                <Link
                  target={openInvoiceInNewTab ? '_blank' : '_self'}
                  to={`/invoices/${i.id}/edit`}
                >
                  {formatCell(
                    i.customerName,
                    toIdentifierMask(i.customerIdentifier, i.customerType)
                  )}
                </Link>
              </td>
              <td>{i.transactionNatureName}</td>
              <td>
                <div className='d-flex align-items-baseline gap-1'>
                  {i.isPaid && (
                    <div>
                      <span
                        style={{ fontSize: '0.75rem' }}
                        className='badge bg-success text-uppercase flex-shrink-0'
                      >
                        Pago
                      </span>
                    </div>
                  )}
                  <div className='d-flex flex-column align-items-start'>
                    <InvoiceStatusBadge state={i.statusState} />
                    {isEditable(i.statusState) ? (
                      <small className='text-muted'>
                        <Link
                          to={`/invoice-statuses/${i.statusId}/edit`}
                          target='_blank'
                        >
                          {i.statusLabel}
                        </Link>
                      </small>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </td>
              <td>
                <div className='mt-1 d-flex gap-1 flex-wrap'>
                  {i.invoiceTags &&
                    i.invoiceTags.map((t) => {
                      return (
                        <InvoiceTagBadge key={t.tagId} tag={t} isCollapsable />
                      );
                    })}
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
